/**
 * MobileOverlayBackdrop — shared building block used by MobileDialog
 * and MobileBottomSheet. Centralises:
 *
 *   • backdrop styling (bg-black/70 backdrop-blur-sm)
 *   • tap-outside-to-dismiss with stopPropagation guard
 *   • z-index priorities from Contract §9
 *
 * Phase 1 — Mobile Shell programme. May 14, 2026.
 * Phase 2 Batch 5 follow-up (Feb 28, 2026): local Z map removed —
 * priorities now resolve directly through the canonical Z_CLASS
 * source-of-truth in `./zTiers`. Behavioural values unchanged.
 *
 * Z-index priorities map to the contract layers:
 *   default → 50   (standard modals, Z_CLASS.MODAL)
 *   sheet   → 1100 (bottom sheet, sits over Active Op pill)
 *   urgent  → 9999 (upload / recovery / wakelock — non-dismissable)
 *   critical→ 10000 (PhoneMountGuidance scope)
 */
import { useMobileOverlay } from "../../hooks/useMobileOverlay";
import { Z_CLASS } from "./zTiers";

function resolveZClass(priority) {
  switch (priority) {
    case "sheet":    return Z_CLASS.SHEET;
    case "urgent":   return Z_CLASS.URGENT;
    case "critical": return Z_CLASS.CRITICAL;
    case "default":
    default:         return Z_CLASS.MODAL;
  }
}

export function MobileOverlayBackdrop({
  open,
  onClose,
  priority = "default",
  dismissible = true,
  testid,
  children,
}) {
  const { onBackdropClick } = useMobileOverlay({
    open,
    onClose: dismissible ? onClose : undefined,
    dismissibleOnEsc: dismissible,
  });
  if (!open) return null;
  return (
    <div
      role="presentation"
      data-testid={testid}
      onClick={dismissible ? onBackdropClick : undefined}
      className={`fixed inset-0 ${resolveZClass(priority)} bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center overscroll-contain`}
    >
      {children}
    </div>
  );
}

export default MobileOverlayBackdrop;
